/* iter55g31 · g32 — Royalties widget for MyBillingPanel.
 *
 * Shows the author's Publish-to-Stacks earnings — lifetime gross,
 * their 85% share, pending payout balance, and a per-book breakdown
 * for the last 30 days. Also renders the Stripe Connect onboarding
 * CTA + status pill so authors can start receiving direct payouts
 * without any admin intervention.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BookOpen, TrendingUp, DollarSign, ExternalLink, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const usd = (cents) => `$${(cents / 100).toFixed(2)}`;

export default function RoyaltiesCard() {
  const [data, setData]         = useState(null);
  const [connect, setConnect]   = useState(null);
  const [busy, setBusy]         = useState(false);
  useEffect(() => {
    axios.get(`${API}/authors/me/royalties`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => setData(null));
    axios.get(`${API}/authors/me/connect/status`, { withCredentials: true })
      .then((r) => setConnect(r.data))
      .catch(() => setConnect({ connected: false }));
  }, []);
  const startConnect = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/authors/me/connect/onboarding-link`,
        { return_url: window.location.href },
        { withCredentials: true },
      );
      if (r.data?.onboarding_url) {
        window.location.href = r.data.onboarding_url;
      } else {
        toast.error('Could not open Stripe onboarding.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start Stripe onboarding.');
    } finally { setBusy(false); }
  };

  if (!data) return null;
  const hasEarnings = (data.lifetime_author_cents || 0) > 0
      || (data.last_30_days || []).length > 0;
  const payoutsReady = connect?.connected && connect?.payouts_enabled;
  if (!hasEarnings && !connect?.connected) return null;

  return (
    <div className="rounded-md border border-primary/40 bg-primary/5"
         data-testid="billing-royalties-card">
      <div className="flex items-center gap-2 border-b border-primary/30 px-4 py-2">
        <TrendingUp className="h-3.5 w-3.5 text-primary" />
        <div className="flex-1 text-[12px] font-semibold">Author royalties</div>
        <span className="rounded-sm bg-primary/15 px-1.5 py-0.5 text-[9.5px] uppercase tracking-wider text-primary">
          {data.author_pct}/{data.platform_pct} split
        </span>
      </div>
      <div className="grid grid-cols-3 gap-3 p-4">
        <Stat icon={<DollarSign className="h-3 w-3" />}
              label="Lifetime earned"  value={usd(data.lifetime_author_cents)} />
        <Stat icon={<BookOpen className="h-3 w-3" />}
              label="Pending payout"   value={usd(data.pending_payout_cents)} />
        <Stat icon={<TrendingUp className="h-3 w-3" />}
              label="Gross (lifetime)" value={usd(data.lifetime_gross_cents)} />
      </div>

      <div className="flex items-center gap-2 border-t border-primary/20 px-4 py-2 text-[11.5px]"
           data-testid="billing-stripe-connect">
        {payoutsReady ? (
          <>
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
            Stripe payouts enabled — future royalties transfer automatically.
          </>
        ) : connect?.connected ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-500" />
            <span className="flex-1">Stripe onboarding in progress. Complete KYC to enable automatic payouts.</span>
            <Button size="sm" variant="ghost" disabled={busy} onClick={startConnect}
                    className="h-7 gap-1 text-[11px]"
                    data-testid="billing-connect-resume-btn">
              <ExternalLink className="h-3 w-3" /> Resume
            </Button>
          </>
        ) : (
          <>
            <span className="flex-1">Set up Stripe payouts to receive your 85% share automatically.</span>
            <Button size="sm" onClick={startConnect} disabled={busy}
                    className="h-7 gap-1 text-[11px]"
                    data-testid="billing-connect-start-btn">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ExternalLink className="h-3 w-3" />}
              Connect with Stripe
            </Button>
          </>
        )}
      </div>

      {(data.last_30_days || []).length > 0 && (
        <div className="border-t border-primary/20 px-4 py-3">
          <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
            Last 30 days
          </div>
          <ul className="space-y-1 text-[11.5px]">
            {data.last_30_days.map((r) => (
              <li key={r.book_id} className="flex items-center justify-between">
                <span className="truncate">{r.book_id}</span>
                <span className="font-mono-stat">
                  {r.sales_count}× · {usd(r.author_cents)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const Stat = ({ icon, label, value }) => (
  <div>
    <div className="mb-0.5 flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
      {icon} {label}
    </div>
    <div className="font-mono-stat text-[14px] font-semibold">{value}</div>
  </div>
);
